/**
 * 统一图标常量
 * 使用 Ant Design Icons，确保全项目图标风格一致
 *
 * 使用方式:
 * import { ICONS } from '@/constants/icons'
 * <ICONS.ACTION.ADD className="text-lg" />
 *
 * 或解构使用:
 * const { ADD, DELETE } = ICONS.ACTION
 * <ADD /> <DELETE />
 */

import type { SVGProps } from 'react'
import {
  // 导航图标
  AppstoreOutlined,
  RobotOutlined,
  CodeOutlined,
  BgColorsOutlined,
  // 操作图标
  PlusCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  SaveOutlined,
  DownloadOutlined,
  UploadOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  ShareAltOutlined,
  SendOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  // UI 控制图标
  MenuOutlined,
  MenuFoldOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
  MoreOutlined,
  SettingOutlined,
  BellOutlined,
  QuestionCircleOutlined,
  DownOutlined,
  UpOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  // 状态图标
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  // 文件图标
  FolderOutlined,
  FileOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  FileTextOutlined,
  FileMarkdownOutlined,
  FilePdfOutlined,
  PaperClipOutlined,
  // 用户图标
  UserOutlined,
  TeamOutlined,
  LoginOutlined,
  LogoutOutlined,
  LockOutlined,
  // 额外常用图标
  StarOutlined,
  StarFilled,
  HeartOutlined,
  HeartFilled,
  LikeOutlined,
  LikeFilled,
  EyeOutlined,
  EyeInvisibleOutlined,
  LinkOutlined,
  UnorderedListOutlined,
  AppstoreFilled,
  AimOutlined,
  RiseOutlined,
  FireOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  WechatOutlined,
  TwitterOutlined,
  InstagramOutlined,
  InboxOutlined,
} from '@ant-design/icons'

// 自定义：侧边栏切换图标（双栏样式）
const SidebarPanelIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" fill="none" />
    <line x1="13.5" y1="3" x2="13.5" y2="21" />
  </svg>
)

// ============================================================
// 导航菜单图标
// ============================================================
export const NAV_ICONS = {
  /** 工作流 */
  WORKFLOW: AppstoreOutlined,
  /** 智能体 */
  AGENT: RobotOutlined,
  /** 运行日志 */
  RUNLOG: CodeOutlined,
  /** 创意画布 */
  CANVAS: BgColorsOutlined,
} as const

// ============================================================
// 操作图标
// ============================================================
export const ACTION_ICONS = {
  /** 添加/新增 */
  ADD: PlusCircleOutlined,
  /** 删除 */
  DELETE: DeleteOutlined,
  /** 编辑 */
  EDIT: EditOutlined,
  /** 复制 */
  COPY: CopyOutlined,
  /** 保存 */
  SAVE: SaveOutlined,
  /** 下载 */
  DOWNLOAD: DownloadOutlined,
  /** 上传 */
  UPLOAD: UploadOutlined,
  /** 刷新 */
  REFRESH: ReloadOutlined,
  /** 搜索 */
  SEARCH: SearchOutlined,
  /** 筛选 */
  FILTER: FilterOutlined,
  /** 排序 */
  SORT: SortAscendingOutlined,
  /** 分享 */
  SHARE: ShareAltOutlined,
  /** 发送 */
  SEND: SendOutlined,
  /** 播放 */
  PLAY: PlayCircleOutlined,
  /** 暂停 */
  PAUSE: PauseCircleOutlined,
  /** 停止 */
  STOP: StopOutlined,
  /** 收藏 */
  STAR: StarOutlined,
  /** 已收藏 */
  STAR_FILLED: StarFilled,
  /** 目标/意图 */
  TARGET: AimOutlined,
} as const

// ============================================================
// UI 控制图标
// ============================================================
export const UI_ICONS = {
  /** 菜单展开 */
  MENU_EXPAND: MenuOutlined,
  /** 侧边栏收起 */
  SIDEBAR_COLLAPSE: SidebarPanelIcon,
  /** 关闭 */
  CLOSE: CloseCircleOutlined,
  /** 返回 */
  BACK: ArrowLeftOutlined,
  /** 更多 */
  MORE: MoreOutlined,
  /** 设置 */
  SETTINGS: SettingOutlined,
  /** 通知 */
  NOTIFICATION: BellOutlined,
  /** 帮助 */
  HELP: QuestionCircleOutlined,
  /** 展开 */
  EXPAND: DownOutlined,
  /** 收起 */
  COLLAPSE: UpOutlined,
  /** 全屏 */
  FULLSCREEN: FullscreenOutlined,
  /** 退出全屏 */
  EXIT_FULLSCREEN: FullscreenExitOutlined,
  /** 列表视图 */
  LIST: UnorderedListOutlined,
  /** 网格视图 */
  GRID: AppstoreFilled,
  /** 链接 */
  LINK: LinkOutlined,
  /** 收件箱/上传区域 */
  INBOX: InboxOutlined,
} as const

// ============================================================
// 状态图标
// ============================================================
export const STATUS_ICONS = {
  /** 成功 */
  SUCCESS: CheckCircleOutlined,
  /** 警告 */
  WARNING: ExclamationCircleOutlined,
  /** 错误 */
  ERROR: CloseCircleOutlined,
  /** 信息 */
  INFO: InfoCircleOutlined,
  /** 加载中 */
  LOADING: LoadingOutlined,
  /** 待处理 */
  PENDING: ClockCircleOutlined,
  /** 上升趋势 */
  TREND_UP: RiseOutlined,
  /** 热门 */
  HOT: FireOutlined,
  /** 闪电/快速 */
  FLASH: ThunderboltOutlined,
} as const

// ============================================================
// 文件类型图标
// ============================================================
export const FILE_ICONS = {
  /** 文件夹 */
  FOLDER: FolderOutlined,
  /** 文件 */
  FILE: FileOutlined,
  /** 图片 */
  IMAGE: PictureOutlined,
  /** 视频 */
  VIDEO: VideoCameraOutlined,
  /** 音频 */
  AUDIO: SoundOutlined,
  /** 文档 */
  DOCUMENT: FileTextOutlined,
  /** 代码 */
  CODE: FileMarkdownOutlined,
  /** PDF */
  PDF: FilePdfOutlined,
  /** 附件 */
  ATTACHMENT: PaperClipOutlined,
} as const

// ============================================================
// 用户相关图标
// ============================================================
export const USER_ICONS = {
  /** 用户 */
  USER: UserOutlined,
  /** 用户组 */
  USERS: TeamOutlined,
  /** 登录 */
  LOGIN: LoginOutlined,
  /** 登出 */
  LOGOUT: LogoutOutlined,
  /** 密码 */
  PASSWORD: LockOutlined,
} as const

// ============================================================
// 社交分享图标
// ============================================================
export const SOCIAL_ICONS = {
  /** 全球/飞书 */
  GLOBAL: GlobalOutlined,
  /** 微信 */
  WECHAT: WechatOutlined,
  /** Twitter */
  TWITTER: TwitterOutlined,
  /** Instagram/小红书 */
  INSTAGRAM: InstagramOutlined,
} as const

// ============================================================
// 交互图标
// ============================================================
export const INTERACTION_ICONS = {
  /** 喜欢 */
  LIKE: LikeOutlined,
  /** 已喜欢 */
  LIKE_FILLED: LikeFilled,
  /** 收藏 */
  HEART: HeartOutlined,
  /** 已收藏 */
  HEART_FILLED: HeartFilled,
  /** 可见 */
  VISIBLE: EyeOutlined,
  /** 隐藏 */
  HIDDEN: EyeInvisibleOutlined,
} as const

// ============================================================
// 聚合导出
// ============================================================
export const ICONS = {
  NAV: NAV_ICONS,
  ACTION: ACTION_ICONS,
  UI: UI_ICONS,
  STATUS: STATUS_ICONS,
  FILE: FILE_ICONS,
  USER: USER_ICONS,
  SOCIAL: SOCIAL_ICONS,
  INTERACTION: INTERACTION_ICONS,
} as const
